/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
  Icon,
  Button,
  Center,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Switch,
  Td,
} from "@chakra-ui/react";
import { FaStore, FaEnvelope, FaPhoneAlt, FaCheckCircle, FaBan, FaEdit, FaUser, FaBuilding, FaCreditCard } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useSuperAdminPermissions } from "../../../hooks/useSuperAdminPermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";


export default function Stores() {
  const { t } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();
  const toast = useToast();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const perms = useSuperAdminPermissions();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        // Use proxy in development, full URL in production


        const res = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`, {
          method: "GET",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        // Try to parse response
        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse JSON response:", parseError);
            console.error("Response text:", text);
            throw new Error("Invalid response format");
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              throw new Error("Invalid response format");
            }
          }
        }

        if (Array.isArray(data)) {
          // Use data directly without translation
          setStores(
            data.map((t) => ({
              tenantId: t.TenantId || t.tenantId,
              tenantName: t.TenantName || t.tenantName || "",
              engTenantName: t.EngTenantName || t.engTenantName || "",
              email: t.Email || t.email || "",
              phone: t.Phone || t.phone || "",
              subDomain: t.SubDomain || "",
              isActive: t.IsActive !== undefined ? t.IsActive : (t.isActive !== undefined ? t.isActive : 1),
              companyName: t.CompanyName || t.companyName || "",
              price: Number(t.Price || t.price) || 0,
              descripsion: t.Descripsion || t.descripsion || "",
              engDescripsion: t.EngDescripsion || t.engDescripsion || "",
              // Additional fields for UpdateTenantFullDetails API
              createdAt: t.CreatedAt || t.createdAt || "",
              product: t.Product || t.product || "",
              mainPlanId: t.MainPlanId || t.mainPlanId || t.PlanId || t.planId || 0,
              tenantDtlId: t.TenantDtlId || t.tenantDtlId || 0,
              logo: t.Logo || t.logo || "",
              clientName: t.ClientName || t.clientName || "",
              sectorType: t.SectorType || t.sectorType || "",
              businessName: t.BusinessName || t.businessName || t.CompanyName || t.companyName || "",
              themeId: t.ThemeId || t.themeId || 0,
              detailPlanId: t.DetailPlanId || t.detailPlanId || t.PlanId || t.planId || 0,
              planType: t.PlanType || t.planType || "",
              engPlanType: t.EngPlanType || t.engPlanType || "",
              note1: t.Note1 || t.note1 || "",
              note2: t.Note2 || t.note2 || "",
              note3: t.Note3 || t.note3 || "",
              note4: t.Note4 || t.note4 || "",
              note5: t.Note5 || t.note5 || "",
              note6: t.Note6 || t.note6 || "",
              note7: t.Note7 || t.note7 || "",
              note8: t.Note8 || t.note8 || "",
              planId: t.PlanId || t.planId || 0,
              paymentMethods: Array.isArray(t.PaymentMethods)
                ? t.PaymentMethods
                : (Array.isArray(t.paymentMethods) ? t.paymentMethods : []),
            }))
          );
        } else {
          console.warn("Expected array but got:", typeof data);
          setStores([]);
        }
      } catch (err) {
        console.error("GetAllTenantFullDetails error:", err);
        toast({
          title: isArabic ? "فشل تحميل البيانات" : "Failed to load stores",
          description: err.message || (isArabic ? "حدث خطأ أثناء تحميل المتاجر" : "An error occurred while loading stores"),
          status: "error",
          duration: 5000,
        });
        setStores([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, [isArabic, baseUrl, toast]);

  if (!perms.viewAdminStores) {
    return <NoAccess />; // صفحة منع الوصول
  }

  const handleEdit = (store) => {
    setSelectedStore({ ...store });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedStore) return;
    setSaving(true);

    try {

      const token = localStorage.getItem("authToken");

      const formData = new FormData();


      // Append all normal fields
      Object.entries({
        TenantId: selectedStore.tenantId || "",
        TenantName: selectedStore.tenantName || "",
        EngTenantName: selectedStore.engTenantName || "",
        Email: selectedStore.email || "",
        Phone: selectedStore.phone || "",
        SubDomain: selectedStore.subDomain || "",
        IsActive: selectedStore.isActive ?? 1,
        CreatedAt: selectedStore.createdAt || "",
        Product: selectedStore.product || "",
        MainPlanId: selectedStore.mainPlanId || selectedStore.planId || 0,
        TenantDtlId: selectedStore.tenantDtlId || 0,
        ClientName: selectedStore.clientName || "",
        SectorType: selectedStore.sectorType || "",
        BusinessName: selectedStore.businessName || "",
        CompanyName: selectedStore.companyName || "",
        ThemeId: selectedStore.themeId || 0,
        ColorName: selectedStore.colorName || "",
        DetailPlanId: selectedStore.detailPlanId || 0,
        PlanType: selectedStore.planType || "",
        EngPlanType: selectedStore.engPlanType || "",
        Price: selectedStore.price || 0,
        Descripsion: selectedStore.descripsion || "",
        EngDescripsion: selectedStore.engDescripsion || "",
        Note1: selectedStore.note1 || "",
        Note2: selectedStore.note2 || "",
        Note3: selectedStore.note3 || "",
        Note4: selectedStore.note4 || "",
        Note5: selectedStore.note5 || "",
        Note6: selectedStore.note6 || "",
        Note7: selectedStore.note7 || "",
        Note8: selectedStore.note8 || "",
      }).forEach(([key, val]) => formData.append(key, val));

      // 🔥 NEW — LOGO HANDLING
      if (selectedStore.logoFile) {
        formData.append("LogoFile", selectedStore.logoFile); // File selected
      } else if (selectedStore.logo) {
        formData.append("Logo", selectedStore.logo); // Old logo name
      }

      // payment methods
      if (Array.isArray(selectedStore.paymentMethods)) {
        formData.append("PaymentMethods", JSON.stringify(selectedStore.paymentMethods));
      }

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      const resultData = await res.json();
      if (!res.ok) throw new Error(resultData?.message || "Save failed");

      toast({
        title: isArabic ? "تم التحديث بنجاح" : "Updated successfully",
        status: "success",
        duration: 3000,
      });

      setStores(prev =>
        prev.map(s =>
          s.tenantId === selectedStore.tenantId ? selectedStore : s
        )
      );

      setIsModalOpen(false);

    } catch (err) {
      toast({
        title: isArabic ? "فشل الحفظ" : "Save Failed",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };



  const toggleStoreStatus = async (store) => {
    const updated = { ...store, isActive: store.isActive ? 0 : 1 };

    try {
      const token = localStorage.getItem("authToken");


      // ✅ تحويل كامل إلى FormData (multipart/form-data)
      const formData = new FormData();

      Object.entries({
        TenantId: updated.tenantId || "",
        TenantName: updated.tenantName || "",
        EngTenantName: updated.engTenantName || "",
        Email: updated.email || "",
        Phone: updated.phone || "",
        SubDomain: updated.subDomain || "",
        IsActive: updated.isActive === 1 || updated.isActive === true ? 1 : 0,
        CreatedAt: updated.createdAt || new Date().toISOString(),
        Product: updated.product || "",
        MainPlanId: updated.mainPlanId || updated.planId || 0,
        TenantDtlId: updated.tenantDtlId || 0,
        ClientName: updated.clientName || "",
        SectorType: updated.sectorType || "",
        BusinessName: updated.businessName || "",
        CompanyName: updated.companyName || "",
        ThemeId: updated.themeId || 0,
        DetailPlanId: updated.detailPlanId || 0,
        PlanType: updated.planType || "",
        EngPlanType: updated.engPlanType || "",
        Price: updated.price || 0,
        Descripsion: updated.descripsion || "",
        EngDescripsion: updated.engDescripsion || "",
        Note1: updated.note1 || "",
        Note2: updated.note2 || "",
        Note3: updated.note3 || "",
        Note4: updated.note4 || "",
        Note5: updated.note5 || "",
        Note6: updated.note6 || "",
        Note7: updated.note7 || "",
        Note8: updated.note8 || "",
        ColorName: updated.colorName || "",
      }).forEach(([key, val]) => formData.append(key, val));

      if (Array.isArray(updated.paymentMethods)) {
        formData.append("PaymentMethods", JSON.stringify(updated.paymentMethods));
      }

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          // ✅ لا تضع Content-Type أبداً مع FormData
        },
        body: formData,
      });

      const resultData = await res.json();

      if (!res.ok) {
        throw new Error(
          resultData?.message ||
          resultData?.error ||
          resultData?.Message ||
          (isArabic ? "فشل تحديث حالة المتجر" : "Failed to update store status")
        );
      }

      // ✅ تحديث الحالة محليًا بعد النجاح
      setStores((prev) =>
        prev.map((s) =>
          s.tenantId === store.tenantId ? updated : s
        )
      );

      toast({
        title: updated.isActive
          ? isArabic ? "تم التفعيل ✅" : "Activated ✅"
          : isArabic ? "تم الإيقاف ✅" : "Deactivated ✅",
        description:
          resultData?.message ||
          (updated.isActive
            ? isArabic ? "تم تفعيل المتجر" : "Store activated"
            : isArabic ? "تم إيقاف المتجر" : "Store deactivated"),
        status: updated.isActive ? "success" : "warning",
        duration: 3000,
      });

    } catch (err) {
      let errorMessage = err.message || String(err);

      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("400")) {
        errorMessage = isArabic
          ? "بيانات غير صحيحة. يرجى التحقق من الحقول المطلوبة"
          : "Invalid data. Please check required fields";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
          : "Server error. Please try again later";
      }

      toast({
        title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    }
  };


  if (loading)
    return (
      <Center minH="80vh">
        <Spinner size="xl" color={colors.mainColor} />
      </Center>
    );

  // Get display name based on language
  const getDisplayName = (store) => {
    return store.clientName || (isArabic ? (store.tenantName || store.companyName) : (store.engTenantName || store.tenantName || store.companyName));
  };

  return (
    <PageContainer title={isArabic ? "إدارة المتاجر" : "Store Management"}>
      <ResponsiveTable
        columns={[
          { header: isArabic ? "اسم المتجر" : "Store Name", sortKey: "clientName" },
          { header: isArabic ? "اسم صاحب المتجر" : "Owner Name", sortKey: "companyName" },
          { header: isArabic ? "اسم الشركة" : "Company Name", sortKey: "clientName" },
          { header: isArabic ? "البريد الإلكتروني" : "Email", sortKey: "email" },
          { header: isArabic ? "الهاتف" : "Phone", sortKey: "phone" },
          { header: isArabic ? "النطاق" : "SubDomain", sortKey: "domain" },
          { header: isArabic ? "السعر" : "Price", sortKey: "price" },
          { header: isArabic ? "الحالة" : "Status", sortKey: "isActive" },
          { header: isArabic ? "الإجراءات" : "Actions" },
        ]}
        data={stores}
        searchable={true}
        sortable={true}
        filterOptions={{
          field: "isActive",
          options: [
            { value: "", label: isArabic ? "الكل" : "All" },
            { value: "1", label: isArabic ? "نشط" : "Active" },
            { value: "0", label: isArabic ? "غير نشط" : "Inactive" },
          ],
        }}
        renderRow={(store) => (
          <>
            <Td color={colors.textColor} fontWeight="medium">
              <HStack>
                <Icon as={FaStore} color={colors.mainColor} />
                <Text>{getDisplayName(store)}</Text>
              </HStack>
            </Td>
            <Td color={colors.textColor}>
              {store.companyName || store.tenantName ? (
                <HStack>
                  <Icon as={FaUser} color={colors.mainColor} />
                  <Text>{store.companyName || store.tenantName}</Text>
                </HStack>
              ) : (
                <Text color={colors.subText}>—</Text>
              )}
            </Td>
            <Td color={colors.textColor}>
              {store.clientName ? (
                <HStack>
                  <Icon as={FaBuilding} color={colors.mainColor} />
                  <Text>{store.clientName}</Text>
                </HStack>
              ) : (
                <Text color={colors.subText}>—</Text>
              )}
            </Td>
            <Td color={colors.textColor}>{store.email}</Td>
            <Td color={colors.textColor}>{store.phone}</Td>
            <Td color={colors.textColor} maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {store.subDomain}
            </Td>
            <Td color={colors.textColor} fontWeight="semibold" textAlign="center">
              {formatNumber(store.price)} {isArabic ? "د.ع" : "IQD"}
            </Td>
            <Td>
              <Badge
                bg={store.isActive ? colors.badgeSuccess : colors.badgeError}
                color={store.isActive ? colors.badgeSuccessText : colors.badgeErrorText}
                px={3}
                py={1}
                borderRadius="md"
                fontWeight="semibold"
              >
                {store.isActive
                  ? isArabic ? "نشط" : "Active"
                  : isArabic ? "غير نشط" : "Inactive"}
              </Badge>
            </Td>
            <Td>
              <HStack spacing={2}>
                {perms.editAdminStore && (
                  <Button
                    size="sm"
                    leftIcon={<FaEdit />}
                    bg={colors.buttonBg}
                    color={colors.buttonText}
                    _hover={{ bg: colors.buttonHover }}
                    onClick={() => handleEdit(store)}
                  >
                    {isArabic ? "تعديل" : "Edit"}
                  </Button>
                )}
                {perms.editAdminStore && (
                  <Button
                    size="sm"
                    leftIcon={store.isActive ? <FaBan /> : <FaCheckCircle />}
                    bg={store.isActive ? "red.500" : "green.500"}
                    color="white"
                    _hover={{ opacity: 0.8 }}
                    onClick={() => toggleStoreStatus(store)}
                  >
                    {store.isActive
                      ? isArabic ? "إيقاف" : "Deactivate"
                      : isArabic ? "تفعيل" : "Activate"}
                  </Button>
                )}
              </HStack>
            </Td>
          </>
        )}
        renderCard={(store) => (
          <>
            <HStack justify="space-between" mb={3}>
              <HStack>
                <Icon as={FaStore} color={colors.mainColor} boxSize={5} />
                <Text fontWeight="bold" color={colors.textColor} fontSize="lg">
                  {getDisplayName(store)}
                </Text>
              </HStack>
              <Badge
                bg={store.isActive ? colors.badgeSuccess : colors.badgeError}
                color={store.isActive ? colors.badgeSuccessText : colors.badgeErrorText}
                px={3}
                py={1}
                borderRadius="md"
                fontWeight="semibold"
              >
                {store.isActive
                  ? isArabic ? "نشط" : "Active"
                  : isArabic ? "غير نشط" : "Inactive"}
              </Badge>
            </HStack>

            <Divider my={3} borderColor={colors.dividerColor} />

            <VStack align="start" spacing={2} mb={4}>
              <HStack>
                <Icon as={FaEnvelope} color={colors.mainColor} />
                <Text color={colors.textColor} fontSize="sm">
                  {store.email}
                </Text>
              </HStack>
              <HStack>
                <Icon as={FaPhoneAlt} color={colors.mainColor} />
                <Text color={colors.textColor} fontSize="sm">
                  {store.phone}
                </Text>
              </HStack>
              <Text color={colors.textColor} fontSize="sm">
                <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                  {isArabic ? "النطاق: " : "Domain: "}
                </Text>
                {store.subDomain}
              </Text>
              <Text color={colors.textColor} fontSize="sm" fontWeight="semibold">
                <Text as="span" color={colors.mainColor}>
                  {isArabic ? "السعر: " : "Price: "}
                </Text>
                {formatNumber(store.price)} IQD
              </Text>
              {(store.companyName || store.tenantName) && (
                <HStack>
                  <Icon as={FaUser} color={colors.mainColor} />
                  <Text color={colors.textColor} fontSize="sm">
                    <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                      {isArabic ? "اسم صاحب المتجر: " : "Owner Name: "}
                    </Text>
                    {store.companyName || store.tenantName}
                  </Text>
                </HStack>
              )}
              {store.clientName && (
                <HStack>
                  <Icon as={FaBuilding} color={colors.mainColor} />
                  <Text color={colors.textColor} fontSize="sm">
                    <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                      {isArabic ? "اسم الشركة: " : "Company Name: "}
                    </Text>
                    {store.clientName}
                  </Text>
                </HStack>
              )}
              {store.paymentMethods && store.paymentMethods.length > 0 && (
                <VStack align="start" spacing={1} w="100%" mt={2}>
                  <Text fontSize="sm" fontWeight="semibold" color={colors.mainColor}>
                    {isArabic ? "طرق الدفع المتاحة: " : "Available Payment Methods: "}
                  </Text>
                  {store.paymentMethods.map((pm, idx) => (
                    <HStack key={idx} w="100%" justify="space-between">
                      <HStack>
                        <Icon as={FaCreditCard} color={colors.mainColor} size="xs" />
                        <Text fontSize="xs" color={colors.textColor}>
                          {isArabic ? (pm.paymentMethodName || pm.name) : (pm.engPaymentMethodName || pm.engName || pm.paymentMethodName || pm.name)}
                        </Text>
                      </HStack>
                      <Badge
                        bg={pm.isActive === 1 || pm.IsActive === 1 ? colors.badgeSuccess : colors.badgeError}
                        color={pm.isActive === 1 || pm.IsActive === 1 ? colors.badgeSuccessText : colors.badgeErrorText}
                        px={2}
                        py={0.5}
                        borderRadius="sm"
                        fontSize="xs"
                      >
                        {pm.isActive === 1 || pm.IsActive === 1
                          ? (isArabic ? "نشط" : "Active")
                          : (isArabic ? "معطل" : "Inactive")}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>

            <HStack spacing={2}>
              <Button
                size="sm"
                leftIcon={<FaEdit />}
                bg={colors.buttonBg}
                color={colors.buttonText}
                _hover={{ bg: colors.buttonHover }}
                flex={1}
                onClick={() => handleEdit(store)}
              >
                {isArabic ? "تعديل" : "Edit"}
              </Button>
              <Button
                size="sm"
                leftIcon={store.isActive ? <FaBan /> : <FaCheckCircle />}
                bg={store.isActive ? "red.500" : "green.500"}
                color="white"
                _hover={{ opacity: 0.8 }}
                flex={1}
                onClick={() => toggleStoreStatus(store)}
              >
                {store.isActive
                  ? isArabic ? "إيقاف" : "Deactivate"
                  : isArabic ? "تفعيل" : "Activate"}
              </Button>
            </HStack>
          </>
        )}
      />

      {selectedStore && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent dir={isArabic ? "rtl" : "ltr"}>
            <ModalHeader>
              {isArabic ? "تعديل بيانات المتجر" : "Edit Store"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>{isArabic ? "اسم المتجر" : "Store Name"}</FormLabel>
                  <Input
                    value={selectedStore.clientName || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        clientName: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "البريد" : "Email"}</FormLabel>
                  <Input
                    value={selectedStore.email || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        email: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "الهاتف" : "Phone"}</FormLabel>
                  <Input
                    value={selectedStore.phone || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        phone: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "النطاق" : "subDomain"}</FormLabel>
                  <Input
                    value={selectedStore.subDomain || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        subDomain: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "اسم صاحب المتجر" : "Owner Name"}</FormLabel>
                  <Input
                    value={selectedStore.companyName || selectedStore.tenantName || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        companyName: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "اسم الشركة" : "Company Name"}</FormLabel>
                  <Input
                    value={selectedStore.clientName || ""}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        clientName: e.target.value,
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{isArabic ? "السعر" : "Price"}</FormLabel>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={selectedStore.price || 0}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        price: Math.max(0, parseInt(e.target.value, 10) || 0),
                      })
                    }
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>

                {selectedStore.paymentMethods && selectedStore.paymentMethods.length > 0 && (
                  <Box w="100%" mt={2}>
                    <FormLabel mb={3} fontWeight="semibold">
                      {isArabic ? "طرق الدفع المتاحة" : "Available Payment Methods"}
                    </FormLabel>
                    <VStack align="stretch" spacing={2}>
                      {selectedStore.paymentMethods.map((pm, idx) => (
                        <HStack key={idx} justify="space-between" p={3} bg={colors.hoverBg} borderRadius="md">
                          <HStack>
                            <Icon as={FaCreditCard} color={colors.mainColor} />
                            <Text fontSize="sm" color={colors.textColor}>
                              {isArabic ? (pm.paymentMethodName || pm.name) : (pm.engPaymentMethodName || pm.engName || pm.paymentMethodName || pm.name)}
                            </Text>
                          </HStack>
                          <FormControl display="flex" alignItems="center" w="auto">
                            <FormLabel mb="0" fontSize="sm" mr={2}>
                              {isArabic ? "نشط" : "Active"}
                            </FormLabel>
                            <Switch
                              isChecked={pm.isActive === 1 || pm.IsActive === 1}
                              onChange={(e) => {
                                const updatedPaymentMethods = [...selectedStore.paymentMethods];
                                updatedPaymentMethods[idx] = {
                                  ...pm,
                                  isActive: e.target.checked ? 1 : 0,
                                  IsActive: e.target.checked ? 1 : 0,
                                };
                                setSelectedStore({
                                  ...selectedStore,
                                  paymentMethods: updatedPaymentMethods,
                                });
                              }}
                            />
                          </FormControl>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">
                    {isArabic ? "نشط" : "Active"}
                  </FormLabel>
                  <Switch
                    isChecked={selectedStore.isActive === 1}
                    onChange={(e) =>
                      setSelectedStore({
                        ...selectedStore,
                        isActive: e.target.checked ? 1 : 0,
                      })
                    }
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter justifyContent="center" gap={6}>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
              >
                {isArabic ? "حفظ" : "Save"}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                {isArabic ? "إلغاء" : "Cancel"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}
